import React, { useState } from 'react';
import {
  Card,
  Input,
  Button,
  List,
  Space,
  Tag,
  message,
  Collapse,
  Typography,
  Spin,
  Empty,
  Tooltip
} from 'antd';
import { SearchOutlined, UserOutlined, BankOutlined, RightOutlined, LinkOutlined } from '@ant-design/icons';
import axios from 'axios';
import { API_SERVIDOR2 } from './config/api';

interface SociosPageProps {
  onSelectCNPJ?: (cnpj: string) => void;
}

const { Search } = Input;
const { Text, Title } = Typography;
const { Panel } = Collapse;

interface Socio {
  id: string;
  cnpj_basico: string;
  nome_socio: string;
  cpf_cnpj_socio: string;
  cpf_cnpj_socio_masked: string;
  qualificacao_descricao: string;
  data_entrada?: string;
  percentual_capital?: number;
}

interface Empresa {
  cnpj_basico: string;
  cnpj_completo?: string;
  razao_social: string;
  nome_fantasia?: string;
  situacao: string;
  uf?: string;
  municipio?: string;
  qualificacao_socio: string;
  data_entrada?: string;
  percentual_capital?: number;
}

interface SocioAgrupado {
  cpf: string;
  nome: string;
  cpf_masked: string;
  total_empresas: number;
  empresas?: Empresa[];
  loading?: boolean;
}

const SociosPage: React.FC<SociosPageProps> = ({ onSelectCNPJ }) => {
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [socios, setSocios] = useState<SocioAgrupado[]>([]);
  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);

  const buscarSocios = async (query: string) => {
    if (!query.trim()) {
      message.warning('Digite um nome para buscar');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(`${API_SERVIDOR2.SOCIOS_SEARCH}`, {
        params: { q: query, limit: 50 }
      });

      if (response.data && response.data.hits) {
        // Agrupar por nome completo (chave única)
        const sociosPorNome: { [key: string]: SocioAgrupado } = {};
        
        response.data.hits.forEach((socio: Socio) => {
          const nomeCompleto = socio.nome_socio;
          if (!sociosPorNome[nomeCompleto]) {
            sociosPorNome[nomeCompleto] = {
              cpf: socio.cpf_cnpj_socio,
              nome: nomeCompleto,
              cpf_masked: socio.cpf_cnpj_socio_masked,
              total_empresas: 0,
              empresas: []
            };
          }
          sociosPorNome[nomeCompleto].total_empresas++;
        });

        const sociosArray = Object.values(sociosPorNome);
        setSocios(sociosArray);
        message.success(`${response.data.total.toLocaleString()} registros encontrados!`);
      } else {
        setSocios([]);
        message.info('Nenhum sócio encontrado');
      }
    } catch (error) {
      console.error('Erro ao buscar sócios:', error);
      message.error('Erro ao buscar sócios');
      setSocios([]);
    } finally {
      setLoading(false);
    }
  };

  const carregarEmpresas = async (nome: string, cpf: string, index: number) => {
    // Atualizar estado para mostrar loading
    const novosSocios = [...socios];
    novosSocios[index].loading = true;
    setSocios(novosSocios);

    try {
      const response = await axios.get(`${API_SERVIDOR2.BASE}/api/v1/socios/nome/${encodeURIComponent(nome)}`, {
        params: { cpf, limit: 50 }
      });

      if (response.data && response.data.empresas) {
        const novosSocios = [...socios];
        novosSocios[index].empresas = response.data.empresas;
        novosSocios[index].loading = false;
        setSocios(novosSocios);
      }
    } catch (error) {
      console.error('Erro ao carregar empresas:', error);
      message.error('Erro ao carregar empresas');
      const novosSocios = [...socios];
      novosSocios[index].loading = false;
      setSocios(novosSocios);
    }
  };

  const handlePanelChange = (keys: string | string[]) => {
    const keysArray = Array.isArray(keys) ? keys : [keys];
    setExpandedKeys(keysArray);

    // Carregar empresas quando expandir
    keysArray.forEach(key => {
      const index = parseInt(key);
      if (!socios[index].empresas || socios[index].empresas!.length === 0) {
        carregarEmpresas(socios[index].nome, socios[index].cpf, index);
      }
    });
  };

  return (
    <div style={{ padding: '0 24px' }}>
      <Card>
        <Title level={2}>
          <UserOutlined /> Busca de Sócios
        </Title>
        
        <div style={{ 
          background: '#e6f7ff', 
          padding: '16px', 
          borderRadius: '8px',
          marginBottom: '24px'
        }}>
          <Text strong>Busque sócios por nome e veja todas as empresas onde participam!</Text>
          <ul style={{ marginTop: '8px', marginBottom: 0 }}>
            <li>Digite o nome completo ou parcial</li>
            <li>Veja CPF mascarado (privacidade)</li>
            <li>Liste todas as empresas do sócio</li>
          </ul>
        </div>

        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <Search
            placeholder="Ex: João Silva, Maria Santos..."
            enterButton={<><SearchOutlined /> Buscar</>}
            size="large"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onSearch={buscarSocios}
            loading={loading}
          />

          {loading && (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <Spin size="large" />
              <div style={{ marginTop: '16px' }}>Buscando sócios...</div>
            </div>
          )}

          {!loading && socios.length > 0 && (
            <div>
              <Text type="secondary">
                {socios.length} sócio(s) encontrado(s)
              </Text>
              
              <Collapse 
                accordion={false}
                activeKey={expandedKeys}
                onChange={handlePanelChange}
                style={{ marginTop: '16px' }}
              >
                {socios.map((socio, index) => (
                  <Panel
                    key={index.toString()}
                    header={
                      <div>
                        <div style={{ fontSize: '16px', fontWeight: 500 }}>
                          <UserOutlined /> {socio.nome}
                        </div>
                        <div style={{ fontSize: '13px', color: '#666', marginTop: '4px' }}>
                          CPF: {socio.cpf_masked} | {socio.total_empresas} empresa(s)
                        </div>
                      </div>
                    }
                  >
                    {socio.loading && (
                      <div style={{ textAlign: 'center', padding: '20px' }}>
                        <Spin />
                        <div style={{ marginTop: '8px' }}>Carregando empresas...</div>
                      </div>
                    )}

                    {!socio.loading && socio.empresas && socio.empresas.length > 0 && (
                      <List
                        dataSource={socio.empresas}
                        renderItem={(empresa) => (
                          <List.Item
                            style={{ 
                              cursor: onSelectCNPJ ? 'pointer' : 'default',
                              padding: '12px 16px',
                              borderRadius: '8px',
                              marginBottom: '8px',
                              background: '#fafafa',
                              transition: 'all 0.2s'
                            }}
                            onClick={() => onSelectCNPJ && onSelectCNPJ(empresa.cnpj_completo || empresa.cnpj_basico)}
                            onMouseEnter={(e) => e.currentTarget.style.background = '#e6f7ff'}
                            onMouseLeave={(e) => e.currentTarget.style.background = '#fafafa'}
                            actions={onSelectCNPJ ? [
                              <Tooltip title="Ver detalhes da empresa">
                                <Button 
                                  type="primary" 
                                  size="small"
                                  icon={<RightOutlined />}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onSelectCNPJ(empresa.cnpj_completo || empresa.cnpj_basico);
                                  }}
                                >
                                  Abrir
                                </Button>
                              </Tooltip>
                            ] : undefined}
                          >
                            <List.Item.Meta
                              avatar={<BankOutlined style={{ fontSize: '28px', color: '#1890ff' }} />}
                              title={
                                <div>
                                  <Text strong style={{ fontSize: '15px' }}>
                                    {empresa.razao_social || `Empresa ${empresa.cnpj_basico}`}
                                  </Text>
                                  {empresa.nome_fantasia && empresa.nome_fantasia !== empresa.razao_social && (
                                    <Text type="secondary" style={{ marginLeft: '8px', fontSize: '13px' }}>
                                      ({empresa.nome_fantasia})
                                    </Text>
                                  )}
                                </div>
                              }
                              description={
                                <Space direction="vertical" size="small" style={{ marginTop: '4px' }}>
                                  <Space wrap>
                                    <Tag color="blue">
                                      CNPJ: {empresa.cnpj_completo || empresa.cnpj_basico}
                                    </Tag>
                                    <Tag color={
                                      empresa.situacao === 'ATIVA' || empresa.situacao === '02' ? 'green' : 
                                      empresa.situacao === 'BAIXADA' ? 'red' : 'default'
                                    }>
                                      {empresa.situacao === '02' ? 'ATIVA' : empresa.situacao || 'N/A'}
                                    </Tag>
                                    {empresa.uf && <Tag color="purple">{empresa.uf}</Tag>}
                                    {empresa.municipio && <Tag>{empresa.municipio}</Tag>}
                                  </Space>
                                  <Text type="secondary" style={{ fontSize: '13px' }}>
                                    <strong>Qualificação:</strong> {empresa.qualificacao_socio || 'Não informada'}
                                  </Text>
                                  {empresa.data_entrada && (
                                    <Text type="secondary" style={{ fontSize: '12px' }}>
                                      Entrada: {empresa.data_entrada.replace(/(\d{4})(\d{2})(\d{2})/, '$3/$2/$1')}
                                    </Text>
                                  )}
                                </Space>
                              }
                            />
                          </List.Item>
                        )}
                      />
                    )}

                    {!socio.loading && (!socio.empresas || socio.empresas.length === 0) && (
                      <Empty description="Nenhuma empresa encontrada" />
                    )}
                  </Panel>
                ))}
              </Collapse>
            </div>
          )}

          {!loading && socios.length === 0 && searchQuery && (
            <Empty
              description="Nenhum sócio encontrado"
              style={{ marginTop: '40px' }}
            />
          )}
        </Space>
      </Card>
    </div>
  );
};

export default SociosPage;
